import type { TaskStatus } from './enums'
import { weekRange } from './iso-week'
import { isOpen } from './visibility'

/**
 * Desempenho mensal (meta padrão 70%) — mesma régua do protótipo:
 *
 *   desempenho = entregue ÷ previsto
 *
 * - Conta as tarefas cujo prazo cai no mês (sem prazo: mês da segunda-feira
 *   da semana de lançamento).
 * - Cancelada sai do previsto.
 * - Aberta com prazo no futuro ainda não conta (entra em `noPrazo`); só passa a
 *   contar quando o prazo vence. Aberta vencida conta com o que já entregou.
 * - Realizada com entrega parcial conta pela `qtd_entregue`.
 *
 * O banco tem a mesma conta em `supply.performance_month` — os dois são
 * testados com os mesmos casos.
 */

export interface PerformanceTask {
  readonly id: string
  readonly title: string
  readonly status: TaskStatus
  readonly prazo: string | null
  readonly semana_lancamento: string
  readonly qtd_prevista: number
  readonly qtd_entregue: number
}

export interface PerformanceRow {
  readonly previsto: number
  readonly entregue: number
  readonly cancelado: number
  /** Tarefas abertas ainda dentro do prazo (não entram no cálculo). */
  readonly noPrazo: number
  /** Tarefas consideradas no mês (todas, inclusive as dentro do prazo). */
  readonly tarefas: number
  /** 0–100 ou `null` quando nada venceu ainda. */
  readonly pct: number | null
  /** Abertas com prazo vencido — "não entregues no prazo". */
  readonly naoEntregues: readonly PerformanceTask[]
}

/** Dia que põe a tarefa no período da régua: o prazo; sem prazo, a segunda da semana de lançamento. */
export function performanceDateOfTask(task: Pick<PerformanceTask, 'prazo' | 'semana_lancamento'>): string {
  return task.prazo ?? weekRange(task.semana_lancamento).monday
}

export function monthOfTask(task: Pick<PerformanceTask, 'prazo' | 'semana_lancamento'>): string {
  return performanceDateOfTask(task).slice(0, 7)
}

/** Primeiro e último dia (`YYYY-MM-DD`) de um mês `YYYY-MM`. */
export function monthBounds(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` }
}

/** Desempenho do mês `YYYY-MM` — o intervalo do primeiro ao último dia. */
export function computePerformance(tasks: readonly PerformanceTask[], month: string, today: string): PerformanceRow {
  const { from, to } = monthBounds(month)
  return computePerformanceRange(tasks, from, to, today)
}

/**
 * A mesma régua num intervalo qualquer de dias (`from`–`to`, inclusive): conta
 * as tarefas cujo prazo (ou segunda da semana de lançamento) cai nele. O mês
 * é só o caso particular — no banco, `supply.performance_range`, que o
 * `performance_month` chama.
 */
export function computePerformanceRange(
  tasks: readonly PerformanceTask[],
  from: string,
  to: string,
  today: string,
): PerformanceRow {
  let previsto = 0
  let entregue = 0
  let cancelado = 0
  let noPrazo = 0
  let count = 0
  const naoEntregues: PerformanceTask[] = []

  for (const t of tasks) {
    const day = performanceDateOfTask(t)
    if (day < from || day > to) continue
    count++
    const q = Math.max(0, t.qtd_prevista)
    if (!q) continue
    const open = isOpen(t.status)
    if (open && t.prazo && t.prazo > today) {
      noPrazo++
      continue
    }
    if (t.status === 'cancelada') {
      cancelado += q
      continue
    }
    previsto += q
    entregue += Math.min(q, Math.max(0, t.qtd_entregue))
    if (open) naoEntregues.push(t)
  }

  return {
    previsto,
    entregue,
    cancelado,
    noPrazo,
    tarefas: count,
    pct: previsto ? Math.round((entregue / previsto) * 100) : null,
    naoEntregues,
  }
}

export type PerformanceClass = 'bom' | 'regular' | 'ruim' | 'sem-dados'

export const PERFORMANCE_CLASS_LABEL: Record<PerformanceClass, string> = {
  bom: 'Bom',
  regular: 'Regular',
  ruim: 'Ruim',
  'sem-dados': 'Sem dados',
}

/** Faixa "Regular" padrão: abaixo da meta, até esta distância (Configurações → Metas). */
export const REGULAR_BAND = 20

/**
 * Bom ≥ meta · Regular de (meta − faixa) até a meta · Ruim abaixo.
 * Com meta 70 e faixa 20 reproduz o protótipo: 70+ / 50–69 / <50.
 */
export function classify(pct: number | null, target: number, band: number = REGULAR_BAND): PerformanceClass {
  if (pct === null) return 'sem-dados'
  if (pct >= target) return 'bom'
  if (pct >= target - band) return 'regular'
  return 'ruim'
}

/** Meta do mês para uma área: a específica do mês, a padrão da área, a global do mês, a global. */
export interface TargetRow {
  readonly area_id: string | null
  readonly month: string | null
  readonly target_pct: number
}

export const DEFAULT_TARGET = 70

export function resolveTarget(targets: readonly TargetRow[], areaId: string | null, month: string): number {
  const find = (a: string | null, m: string | null) =>
    targets.find((t) => t.area_id === a && (t.month?.slice(0, 7) ?? null) === m)
  return (
    (areaId ? (find(areaId, month) ?? find(areaId, null)) : undefined)?.target_pct ??
    find(null, month)?.target_pct ??
    find(null, null)?.target_pct ??
    DEFAULT_TARGET
  )
}
